// Safe wrapper around posthog-js.
//
// PostHog is non-critical analytics. Ad blockers, browser extensions, network
// failures, or SDK bugs must NEVER surface as errors to the user.
// Every method here swallows exceptions silently so the booking flow is
// always unaffected by analytics failures.

import posthog from "posthog-js";

export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}

export function identify(
  distinctId: string,
  properties?: Record<string, unknown>,
  setOnceProperties?: Record<string, unknown>,
): void {
  try {
    posthog.identify(distinctId, properties, setOnceProperties);
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}

export function register(properties: Record<string, unknown>): void {
  try {
    posthog.register(properties);
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}

export function setPersonProperties(
  properties: Record<string, unknown | null>,
): void {
  try {
    posthog.setPersonProperties(properties);
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}

export function unregister(property: string): void {
  try {
    posthog.unregister(property);
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}

// Clears the identified person and starts a fresh anonymous distinct_id.
// Must be called on logout so a shared/kiosk device doesn't carry one
// user's identity into the next person's session.
export function reset(): void {
  try {
    posthog.reset();
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}

// Returns true/false if the flag is loaded, or undefined if PostHog is
// unavailable or flags haven't loaded yet. Callers treat undefined as false.
export function isFeatureEnabled(flag: string): boolean | undefined {
  try {
    return posthog.isFeatureEnabled(flag);
  } catch {
    return undefined;
  }
}

// Returns the variant value of a multi-variant flag (string), true/false for
// boolean flags, or undefined if flags haven't loaded or PostHog is unavailable.
export function getFeatureFlag(flag: string): string | boolean | undefined {
  try {
    const value = posthog.getFeatureFlag(flag);
    return value === null || value === undefined ? undefined : (value as string | boolean);
  } catch {
    return undefined;
  }
}

// Registers a callback that fires once PostHog has finished loading all
// feature flags. Use when isFeatureEnabled returns undefined on first render.
export function onFeatureFlags(callback: () => void): void {
  try {
    posthog.onFeatureFlags(callback);
  } catch {
    // Analytics errors must never disrupt the user experience
  }
}
